// Mason Genie cart drawer — real Shopify cart via /cart.js + /cart/add.js + /cart/change.js.
//
// Concurrency model: all UI updates are OPTIMISTIC (instant local render), and server
// responses are only applied when NO local operations are pending or in flight
// (state.pending === 0). Otherwise the response is discarded and a single re-fetch is
// queued for when the dust settles. Desired quantities live in state.desired so an
// in-flight response can never make us sync a stale number.
(function () {
  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // Mason Genie quantity breaks. The discount lands at checkout via a percentage code
  // (created in admin) so it scales to ANY quantity; the cart mirrors the same math
  // (incl. Shopify's per-line rounding) so displayed totals match checkout exactly.
  var MG_CORE = ['mason-genie', 'mason-genie-2', 'mason-genie™-vacuum-sealer-kit-1'];
  function mgTierPct(qty) {
    // Must EXACTLY match the Shopify discount codes (Shopify only allows whole %):
    // BUNDLE2 = 10% off, BUNDLE3 = 15% off. Per-line rounding mirrors checkout.
    if (qty >= 3) return 0.15; // -> $42.49 each off a $49.99 base
    if (qty === 2) return 0.10; // -> $44.99 each
    return 0;
  }
  function mgCheckoutCode(qty) {
    if (qty >= 3) return 'SPECIALOFFER-BUNDLE3';
    if (qty === 2) return 'SPECIALOFFER-BUNDLE2';
    if (qty === 1) return 'SPECIALOFFER-MASONGENIE';
    return null;
  }

  // Replo pages add to cart with their own JS, then call window.monster_refresh()
  // (a Monster Cart hook). Monster is disabled on this theme — route the hook to us.
  // The delayed re-sync covers Replo completing its API call after firing the hook.
  window.monster_refresh = function () {
    document.dispatchEvent(new CustomEvent('vg:cart:open'));
    document.dispatchEvent(new CustomEvent('vg:cart:changed'));
    setTimeout(function () { document.dispatchEvent(new CustomEvent('vg:cart:changed')); }, 900);
  };

  function init(root) {
    if (!root) return;

    var threshold = parseFloat(root.dataset.vgcThreshold || '80') * 100; // cents
    // "CAD" for Canadian visitors (stamped server-side by the drawer section
    // from the same Shopify Markets country context that picks the pricing);
    // appended to the Discounts + Subtotal summary amounts only.
    var curSuffix = root.dataset.vgcCur ? ' ' + root.dataset.vgcCur : '';
    var metaEl = root.querySelector('[data-vgc-meta]');
    var meta = {};
    try { meta = JSON.parse(metaEl.textContent); } catch (e) { console.error('[vg-cart] bad meta JSON', e); }

    var itemsWrap = root.querySelector('[data-vgc-items]');
    var state = {
      cart: null,
      timers: {},     // per-line debounce timers
      desired: {},    // key -> quantity the USER wants (authoritative over any response)
      pending: 0,     // scheduled timers + in-flight mutating requests
      wantRefresh: false,
      epoch: 0        // bumped on every local mutation; stale snapshots are rejected
    };

    // ---- Concurrency helpers ----

    function opStart() { state.pending++; }
    function opEnd() {
      state.pending--;
      if (state.pending <= 0) {
        state.pending = 0;
        if (state.wantRefresh) {
          state.wantRefresh = false;
          refresh();
        }
      }
    }

    function applyServerCart(cart, epochAtRequest) {
      if (state.pending > 0) {
        // Local ops in flight — this data may be stale.
        state.wantRefresh = true;
        return;
      }
      if (epochAtRequest !== undefined && epochAtRequest !== state.epoch) {
        // The user acted AFTER this request started; its snapshot predates them
        // (slow responses on mobile can outlive an entire optimistic cycle).
        refresh();
        return;
      }
      state.cart = cart;
      state.desired = {};
      mergeMgLines();          // visual merge of duplicate Mason Genie listings
      render(state.cart);
      consolidateMgOnServer(); // and physically merge them in the Shopify cart
      ensureMeta(state.cart).then(function () {
        if (state.pending === 0) render(state.cart);
      });
    }

    function refresh() {
      if (state.pending > 0) { state.wantRefresh = true; return; }
      var ep = state.epoch;
      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) { applyServerCart(cart, ep); })
        .catch(function (err) { console.error('[vg-cart] fetch failed', err); });
    }

    // The store has duplicate Mason Genie listings (Replo kit buttons add different
    // ones), which showed as two confusing lines. Merge them into one line locally,
    // then tell Shopify to do the same.
    var consolidating = false;
    function mergeMgLines() {
      // Build the position-indexed quantity array BEFORE merging locally — apps
      // (ABConvert) split the same variant into multiple lines via properties, so
      // consolidation must address lines by INDEX, never by variant or key.
      var mgIdx = [];
      state.cart.items.forEach(function (i, idx) {
        if (MG_CORE.indexOf(i.handle) > -1 && !i.pending) mgIdx.push(idx);
      });
      if (mgIdx.length < 2) return;
      var updates = state.cart.items.map(function (i) { return i.quantity; });
      var total = 0;
      mgIdx.forEach(function (idx) { total += state.cart.items[idx].quantity; });
      mgIdx.forEach(function (idx, n) { updates[idx] = n === 0 ? total : 0; });
      state.needsConsolidation = updates;

      // local visual merge
      var first = state.cart.items[mgIdx[0]];
      first.quantity = total;
      first.final_line_price = first.price * total;
      state.cart.items = state.cart.items.filter(function (i, idx) {
        return idx === mgIdx[0] || mgIdx.indexOf(idx) === -1;
      });
      var count = 0, sub = 0;
      state.cart.items.forEach(function (i) { count += i.quantity; sub += i.price * i.quantity; });
      state.cart.item_count = count;
      state.cart.items_subtotal_price = sub;
    }
    function consolidateMgOnServer() {
      var updates = state.needsConsolidation;
      if (!updates || consolidating) return;
      state.needsConsolidation = null;
      consolidating = true;
      opStart();
      state.epoch++;
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      })
        .then(function (r) { return r.json(); })
        .then(function () { consolidating = false; state.wantRefresh = true; opEnd(); })
        .catch(function () { consolidating = false; state.wantRefresh = true; opEnd(); });
    }

    // Compare-at prices for items outside the embedded funnel meta (non-blocking).
    var fetchedHandles = {};
    function ensureMeta(cart) {
      var missing = [];
      cart.items.forEach(function (it) {
        if (!meta[String(it.variant_id)] && it.handle && !fetchedHandles[it.handle]) missing.push(it.handle);
      });
      if (!missing.length) return Promise.resolve();
      return Promise.all(missing.map(function (h) {
        fetchedHandles[h] = true;
        return fetch('/products/' + h + '.js')
          .then(function (r) { return r.json(); })
          .then(function (prod) {
            (prod.variants || []).forEach(function (v) {
              if (!meta[String(v.id)]) meta[String(v.id)] = { compare: v.compare_at_price || 0 };
            });
          })
          .catch(function () {});
      }));
    }

    // ---- Drawer open/close ----

    function open() {
      root.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (state.cart) render(state.cart);
      refresh();
    }
    function close() {
      root.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    root.querySelector('[data-vgc-close]').addEventListener('click', close);
    root.querySelector('[data-vgc-close-overlay]').addEventListener('click', close);
    document.addEventListener('vg:cart:open', open);
    document.addEventListener('vg:cart:changed', refresh);

    // Optimistic add — forms carrying data-vgc-opt-* attributes (e.g. the home
    // page Quick Add cards) render their line INSTANTLY from the form's own data;
    // the server add reconciles afterwards via the normal refresh path.
    // The line is flagged `pending` so its qty/remove controls stay disabled until
    // the server cart (where lines are addressed by index) actually contains it.
    // opStart() holds the pending gate so an in-flight snapshot that predates the
    // add (open() fires refresh() immediately) can't wipe the optimistic line.
    document.addEventListener('vg:cart:opt-add', function (e) {
      var d = e.detail || {};
      state.epoch++;
      opStart();
      if (!state.cart || !d.id) return; // settle's opEnd still balances the gate
      var it = null;
      state.cart.items.forEach(function (i) {
        if (!it && i.variant_id === d.id && !i.pending) it = i;
      });
      if (it) {
        it.quantity += d.qty;
        it.final_line_price = it.price * it.quantity;
      } else {
        state.cart.items.unshift({
          id: d.id, variant_id: d.id, key: 'vgc-opt-' + d.id,
          product_title: d.title, title: d.title, handle: d.handle, url: d.url || '',
          price: d.price, final_price: d.price, quantity: d.qty,
          line_price: d.price * d.qty, final_line_price: d.price * d.qty,
          original_line_price: d.price * d.qty,
          image: d.image || '', properties: {}, pending: true
        });
      }
      state.cart.item_count += d.qty;
      state.cart.items_subtotal_price += d.price * d.qty;
      state.cart.total_price += d.price * d.qty;
      render(state.cart);
    });
    document.addEventListener('vg:cart:opt-settle', function () {
      state.wantRefresh = true;
      opEnd();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && root.classList.contains('is-open')) close();
    });

    root.querySelector('[data-vgc-checkout]').addEventListener('click', function () {
      var btn = this;
      function goCheckout() {
        var mgQty = 0;
        (state.cart ? state.cart.items : []).forEach(function (i) {
          if (MG_CORE.indexOf(i.handle) > -1) mgQty += i.quantity;
        });
        var code = mgCheckoutCode(mgQty);
        window.location.href = code ? '/discount/' + code + '?redirect=/checkout' : '/checkout';
      }
      // Guard: while an optimistic add or a debounced qty change is still
      // settling, the SERVER cart (which checkout reads) may not yet match what
      // the drawer shows. Hold the navigation until the mutations land — the
      // window is well under a second — with a 4s ceiling so checkout can never
      // get stuck behind a hung request.
      if (state.pending > 0) {
        if (btn.dataset.vgcWaiting) return;
        btn.dataset.vgcWaiting = '1';
        btn.style.opacity = '.6';
        var waited = 0;
        var iv = setInterval(function () {
          waited += 150;
          if (state.pending === 0 || waited >= 4000) {
            clearInterval(iv);
            delete btn.dataset.vgcWaiting;
            btn.style.opacity = '';
            goCheckout();
          }
        }, 150);
        return;
      }
      goCheckout();
    });

    // ---- Mutations ----

    function addVariant(id) {
      state.epoch++;
      opStart();
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
      })
        .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
        .then(function () { state.wantRefresh = true; opEnd(); })
        .catch(function (err) {
          console.error('[vg-cart] add failed', err);
          state.wantRefresh = true;
          opEnd();
        });
    }

    // Debounced per-line sync. Apps like ABConvert rewrite line properties after
    // adds, which ROTATES Shopify's line keys — so a key captured at render time can
    // 404 by click time. We therefore address lines by their 1-based INDEX, resolved
    // at send time from the item object's current position.
    var lidSeq = 1;
    function lidOf(item) {
      if (!item._lid) item._lid = 'L' + (lidSeq++);
      return item._lid;
    }
    function scheduleLineSync(item) {
      var lid = lidOf(item);
      if (state.timers[lid]) {
        clearTimeout(state.timers[lid]); // reschedule within the same pending slot
      } else {
        opStart(); // new pending slot for this line
      }
      state.timers[lid] = setTimeout(function () {
        delete state.timers[lid];
        var qty = state.desired[lid];
        var idx = state.cart ? state.cart.items.indexOf(item) : -1;
        if (idx < 0 && qty > 0) {
          // line vanished locally (shouldn't happen) — resync
          state.wantRefresh = true;
          opEnd();
          return;
        }
        var body = idx >= 0 ? { line: idx + 1, quantity: qty } : { id: item.key, quantity: qty };
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        })
          .then(function (r) { if (!r.ok) throw new Error('change failed'); return r.json(); })
          .then(function () {
            state.wantRefresh = true;
            opEnd();
          })
          .catch(function (err) {
            console.error('[vg-cart] change failed', err);
            state.wantRefresh = true;
            opEnd();
          });
      }, 300);
    }

    function localSetQty(item, qty) {
      if (!state.cart || !item) return;
      state.epoch++;
      qty = Math.max(0, qty);
      // Capture the server line POSITION before mutating local state. ABConvert rotates
      // line keys on every change, so removing by a stale item.key 404s and the item
      // reappears on the next sync. Line index is stable.
      var lineIndex = state.cart.items.indexOf(item);
      state.desired[lidOf(item)] = qty;
      var items = [];
      state.cart.items.forEach(function (i) {
        if (i === item) {
          if (qty <= 0) return; // drop the line locally
          i.quantity = qty;
          i.final_line_price = i.price * qty;
          i.line_price = i.price * qty;
        }
        items.push(i);
      });
      state.cart.items = items;
      recomputeTotals();
      render(state.cart);
      if (qty <= 0) {
        opStart();
        var body = lineIndex >= 0 ? { line: lineIndex + 1, quantity: 0 } : { id: item.key, quantity: 0 };
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        })
          .then(function (r) { if (!r.ok) throw new Error('remove failed'); return r.json(); })
          .then(function () { state.wantRefresh = true; opEnd(); })
          .catch(function (err) { console.error('[vg-cart] remove failed', err); state.wantRefresh = true; opEnd(); });
      } else {
        scheduleLineSync(item);
      }
    }

    function recomputeTotals() {
      var count = 0, sub = 0;
      state.cart.items.forEach(function (i) { count += i.quantity; sub += i.price * i.quantity; });
      state.cart.item_count = count;
      state.cart.items_subtotal_price = sub;
    }

    function localAddSynthetic(card) {
      if (!state.cart) return;
      var vid = parseInt(card.dataset.variantId, 10);
      var exists = state.cart.items.some(function (i) { return i.variant_id === vid; });
      if (exists) return;
      state.cart.items.unshift({
        key: 'pending-' + vid,
        pending: true,
        variant_id: vid,
        handle: card.dataset.vgcHandle || '',
        product_title: card.dataset.vgcTitle || '',
        variant_title: '',
        price: parseInt(card.dataset.vgcPrice, 10) || 0,
        final_line_price: parseInt(card.dataset.vgcPrice, 10) || 0,
        quantity: 1,
        image: card.dataset.vgcImage || ''
      });
      recomputeTotals();
      render(state.cart);
    }

    // Empty-state product cards (Vacuum Genie + Mason Genie)
    root.querySelectorAll('[data-vgc-add-kit]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.classList.contains('is-busy')) return;
        btn.classList.add('is-busy');
        var card = btn.closest('[data-vgc-empty-card]') || btn;
        localAddSynthetic(card);
        addVariant(parseInt((card.dataset.variantId || btn.dataset.variantId), 10)).then(function () {
          btn.classList.remove('is-busy');
        });
      });
    });

    // Upsell adds — instant feedback + synthetic line; server confirms after
    root.querySelectorAll('[data-vgc-upsell]').forEach(function (card) {
      var btn = card.querySelector('[data-vgc-upsell-add]');
      // The Bread Genie wraps card stays addable forever (extra sets at full
      // price) — brief "Added" feedback, then back to ADD. Other cards keep
      // the one-shot Added state.
      var repeatable = card.dataset.vgcGroup === 'bg';
      btn.addEventListener('click', function () {
        if (btn.classList.contains('is-added') && !repeatable) return;
        if (repeatable && btn.dataset.vgcBusy) return;
        btn.classList.add('is-added');
        btn.innerHTML = '&#10003; Added';
        if (repeatable) {
          btn.dataset.vgcBusy = '1';
          setTimeout(function () {
            delete btn.dataset.vgcBusy;
            btn.classList.remove('is-added');
            btn.innerHTML = '<span class="vgc-add-short">ADD</span><span class="vgc-add-full">ADD &amp; SAVE</span>';
          }, 1200);
        }
        localAddSynthetic(card);
        addVariant(parseInt(card.dataset.variantId, 10));
      });
    });

    // ---- Render ----

    function render(cart) {
      var isEmpty = cart.item_count === 0;

      root.querySelectorAll('[data-vgc-count]').forEach(function (el) { el.textContent = cart.item_count; });
      document.querySelectorAll('[data-vg-cart-count]').forEach(function (el) {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });

      root.querySelector('[data-vgc-filled]').classList.toggle('vgc-hidden', isEmpty);
      root.querySelector('[data-vgc-footer]').classList.toggle('vgc-hidden', isEmpty);
      root.querySelector('[data-vgc-empty]').classList.toggle('vgc-hidden', !isEmpty);
      root.querySelector('[data-vgc-empty-footer]').classList.toggle('vgc-hidden', !isEmpty);

      // Line items — Mason Genie core kits get quantity-break effective pricing
      var mgQtyTotal = 0;
      cart.items.forEach(function (i) { if (MG_CORE.indexOf(i.handle) > -1) mgQtyTotal += i.quantity; });
      var mgPct = mgTierPct(mgQtyTotal);

      // Display order only: the free beeswax-wraps gift renders under the
      // Bread Genie bags. state.cart.items is NOT touched — qty/remove ops
      // resolve server line indexes from the authoritative array at call time.
      var displayItems = cart.items.slice();
      var bgGiftHandle = 'bread-genie-bees-wax-wraps';
      if (displayItems.some(function (i) { return i.handle === 'bread-genie'; })) {
        var bgWraps = displayItems.filter(function (i) { return i.handle === bgGiftHandle; });
        if (bgWraps.length) {
          displayItems = displayItems.filter(function (i) { return i.handle !== bgGiftHandle; });
          var lastBread = -1;
          displayItems.forEach(function (i, idx) { if (i.handle === 'bread-genie') lastBread = idx; });
          displayItems.splice.apply(displayItems, [lastBread + 1, 0].concat(bgWraps));
        }
      }

      var compareTotal = 0, displaySubtotal = 0;
      itemsWrap.innerHTML = '';
      displayItems.forEach(function (item) {
        var m = meta[String(item.variant_id)] || {};
        var compareEach = m.compare && m.compare > item.price ? m.compare : item.price;
        compareTotal += compareEach * item.quantity;

        var isMgCore = MG_CORE.indexOf(item.handle) > -1;
        var lineSub = item.price * item.quantity;
        var lineTotal = lineSub;
        var pctLabel = compareEach > item.price ? Math.floor((1 - item.price / compareEach) * 100 + 0.7) + '% OFF' : '';
        if (isMgCore && mgPct > 0) {
          lineTotal = lineSub - Math.round(lineSub * mgPct);
          pctLabel = (mgQtyTotal >= 3 ? '58' : '55') + '% OFF';
        } else if (typeof item.final_line_price === 'number' && item.final_line_price < lineSub) {
          // Automatic discounts (e.g. the free beeswax-wraps gift with a Bread
          // Genie bundle) arrive from /cart.js as a reduced final_line_price —
          // show what checkout will actually charge.
          lineTotal = item.final_line_price;
          if (lineTotal === 0) pctLabel = '';
        }
        displaySubtotal += lineTotal;

        var perUnitHtml = '';
        if (isMgCore && mgQtyTotal >= 2) {
          perUnitHtml = '<div class="vgc-item-perunit">Only ' + money(Math.round(lineTotal / item.quantity)) + ' per kit</div>';
        }

        var row = document.createElement('div');
        row.className = 'vgc-item';
        var img = item.image || '';
        if (img && img.indexOf('width=') === -1) img += (img.indexOf('?') > -1 ? '&' : '?') + 'width=240';
        var subText = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : (item.product_type || '');

        row.innerHTML =
          '<div class="vgc-item-img" style="background-image:url(\'' + img + '\')"></div>' +
          '<div class="vgc-item-info">' +
            '<div class="vgc-item-top">' +
              '<div class="vgc-item-name"></div>' +
              '<button type="button" class="vgc-item-remove" aria-label="Remove" title="Remove">' +
                '<svg width="15" height="16" viewBox="0 0 15 16" fill="none"><path d="M2.5 4h10M6 4V2.8c0-.4.3-.8.8-.8h1.4c.5 0 .8.4.8.8V4m2.3 0v9c0 .7-.5 1.2-1.2 1.2H4.9c-.7 0-1.2-.5-1.2-1.2V4M6 7v4.5M9 7v4.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"></path></svg>' +
              '</button>' +
            '</div>' +
            '<div class="vgc-item-sub"></div>' +
            '<div class="vgc-item-bottom">' +
              '<div class="vgc-item-prices">' +
                '<span class="vgc-item-price">' + (lineTotal === 0 ? 'FREE GIFT' : money(lineTotal)) + '</span>' +
                (compareEach * item.quantity > lineTotal
                  ? '<span class="vgc-item-compare">' + money(compareEach * item.quantity) + '</span>' +
                    (pctLabel ? '<span class="vgc-item-pct">' + pctLabel + '</span>' : '')
                  : '') +
              '</div>' +
              '<div class="vgc-qty">' +
                '<button type="button" data-dec>&minus;</button>' +
                '<span>' + item.quantity + '</span>' +
                '<button type="button" data-inc>+</button>' +
              '</div>' +
            '</div>' +
            perUnitHtml +
          '</div>';

        row.querySelector('.vgc-item-name').textContent = item.product_title;
        row.querySelector('.vgc-item-sub').textContent = subText;
        if (item.pending) {
          row.querySelector('.vgc-item-remove').disabled = true;
        } else {
          row.querySelector('.vgc-item-remove').addEventListener('click', function () { localSetQty(item, 0); });
          row.querySelector('[data-dec]').addEventListener('click', function () { localSetQty(item, item.quantity - 1); });
          row.querySelector('[data-inc]').addEventListener('click', function () { localSetQty(item, item.quantity + 1); });
        }
        itemsWrap.appendChild(row);

        // "Buy 1 more" prompt directly under a single Mason Genie line
        if (isMgCore && mgQtyTotal === 1 && !item.pending) {
          var promo = document.createElement('div');
          promo.className = 'vgc-buymore-wrap';
          promo.innerHTML =
            '<div class="vgc-buymore">' +
              '<div class="vgc-buymore-copy">Buy 1 more and save <strong>' + (curSuffix ? '$14' : '$10') + '</strong> on your order + get <strong>FREE shipping</strong></div>' +
              '<button type="button" class="vgc-buymore-btn">CLAIM OFFER</button>' +
            '</div>';
          promo.querySelector('.vgc-buymore-btn').addEventListener('click', function () {
            localSetQty(item, item.quantity + 1);
          });
          row.style.borderBottom = 'none'; // no divider between the item and its promo
          itemsWrap.appendChild(promo);
        }
      });

      // Free shipping progress (based on what they actually pay)
      var shipMsg = root.querySelector('[data-vgc-ship-msg]');
      var shipFill = root.querySelector('[data-vgc-ship-fill]');
      var unlocked = displaySubtotal >= threshold && cart.item_count > 0;
      if (unlocked) {
        shipMsg.classList.add('is-unlocked');
        shipMsg.innerHTML = '&#10003; Congrats! You\'ve unlocked FREE Shipping!';
        shipFill.classList.add('is-unlocked');
      } else {
        shipMsg.classList.remove('is-unlocked');
        shipMsg.innerHTML = 'You\'re <strong>' + money(Math.max(0, threshold - displaySubtotal)) + '</strong> away from <strong>FREE Shipping</strong>';
        shipFill.classList.remove('is-unlocked');
      }
      shipFill.style.width = Math.min(100, threshold > 0 ? (displaySubtotal / threshold) * 100 : 100) + '%';

      // Order summary (effective prices — matches checkout)
      var savings = compareTotal - displaySubtotal;
      var pctOff = compareTotal > 0 ? Math.floor((savings / compareTotal) * 100 + 0.7) : 0;
      var sumSubEl = root.querySelector('[data-vgc-sum-sub]');
      if (sumSubEl) sumSubEl.textContent = money(compareTotal);
      root.querySelector('[data-vgc-subtotal]').textContent = money(displaySubtotal) + curSuffix;
      root.querySelector('[data-vgc-sum-disc-row]').classList.toggle('vgc-hidden', savings <= 0);
      root.querySelector('[data-vgc-sum-disc]').textContent = '-' + money(savings) + curSuffix;
      root.querySelector('[data-vgc-pct-off]').textContent = pctOff > 0 ? pctOff + '% OFF' : '';

      // Cart-content flags drive upsell groups + trust rows
      var MG_HANDLES = MG_CORE.concat(['mason-genie-liquidchalkpen-offer', 'mason-genie-moms-kit']);
      // Any handle starting with "vacuum-genie" (vacuum-genie, -1, -2, and any
      // future price-test twin) counts as a Vacuum Genie, so its cart upsells
      // stay consistent without needing to hardcode each new handle.
      var mgIn = false, vgIn = false, bgIn = false;
      cart.items.forEach(function (i) {
        if (MG_HANDLES.indexOf(i.handle) > -1) mgIn = true;
        if (i.handle && i.handle.indexOf('vacuum-genie') === 0) vgIn = true;
        if (i.handle && i.handle.indexOf('bread-genie') === 0) bgIn = true;
      });

      var trustMg = root.querySelector('[data-vgc-trust="mg"]');
      var trustVg = root.querySelector('[data-vgc-trust="vg"]');
      if (trustMg) trustMg.classList.toggle('vgc-hidden', !mgIn);
      if (trustVg) trustVg.classList.toggle('vgc-hidden', mgIn);

      // Upsell groups
      var list = root.querySelector('[data-vgc-upsell-list]');
      var titleEl = root.querySelector('[data-vgc-upsells-title]');
      if (list) {
        var byGroup = { bags: [], machines: [], fbt: [], bg: [] };
        list.querySelectorAll('[data-vgc-upsell]').forEach(function (card) {
          var g = card.dataset.vgcGroup;
          if (byGroup[g]) byGroup[g].push(card);
          card.classList.add('vgc-hidden');
        });
        var show = [], title = 'Stock up on bags & save!';
        if (mgIn && vgIn) {
          show = [byGroup.bags[0], byGroup.bags[1]].concat(byGroup.fbt);
        } else if (mgIn) {
          show = byGroup.fbt;
          title = 'Frequently Bought Together';
        } else if (vgIn) {
          show = byGroup.bags;
        } else if (bgIn) {
          // Bread Genie cart: the only frequently-bought-together item is an
          // extra set of beeswax wraps at regular price.
          show = byGroup.bg;
          title = 'Frequently Bought Together';
        } else {
          show = byGroup.machines;
          title = 'Frequently Bought Together';
        }
        if (titleEl) titleEl.textContent = title;
        show.forEach(function (card) {
          if (!card) return;
          card.classList.remove('vgc-hidden');
          list.appendChild(card);
        });

        var inCart = {};
        cart.items.forEach(function (i) { inCart[i.variant_id] = true; });
        list.querySelectorAll('[data-vgc-upsell]').forEach(function (card) {
          var btn = card.querySelector('[data-vgc-upsell-add]');
          var vid = parseInt(card.dataset.variantId, 10);
          if (inCart[vid] && card.dataset.vgcGroup !== 'bg') {
            btn.classList.add('is-added');
            btn.innerHTML = '&#10003; Added';
          } else {
            btn.classList.remove('is-added');
            btn.innerHTML = '<span class="vgc-add-short">ADD</span><span class="vgc-add-full">ADD &amp; SAVE</span>';
          }
        });
      }
    }

    // Debug/QA handle (drives the automated race tests; harmless in production)
    root.__vgc = { state: state, localSetQty: localSetQty, refresh: refresh, addVariant: addVariant };

    // Initial badge sync (drawer stays closed)
    refresh();
  }

  // Add-to-cart takeover — BUBBLE phase, deferring to page frameworks:
  // • If another handler (Replo, Dawn, GemPages) already called preventDefault, it is
  //   handling the add itself (possibly with SET-quantity semantics) — don't interfere;
  //   just open our drawer and re-sync after it finishes.
  // • Otherwise the form would do a full-page native submit — take it over via AJAX.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.getAttribute) return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;

    if (e.defaultPrevented) {
      document.dispatchEvent(new CustomEvent('vg:cart:open'));
      setTimeout(function () { document.dispatchEvent(new CustomEvent('vg:cart:changed')); }, 700);
      setTimeout(function () { document.dispatchEvent(new CustomEvent('vg:cart:changed')); }, 1800);
      return;
    }

    e.preventDefault();
    document.dispatchEvent(new CustomEvent('vg:cart:open'));

    // Optimistic path: only for forms that opted in by carrying their own line
    // data (title/price/etc). Every other add-to-cart form behaves exactly as
    // before — no extra requests, no behavior change.
    var ds = form.dataset || {};
    var optimistic = false;
    if (ds.vgcOptTitle) {
      var idEl = form.querySelector('[name="id"]');
      var qtyEl = form.querySelector('[name="quantity"]');
      var det = {
        id: parseInt(idEl && idEl.value, 10) || 0,
        qty: parseInt(qtyEl && qtyEl.value, 10) || 1,
        title: ds.vgcOptTitle,
        handle: ds.vgcOptHandle || '',
        url: ds.vgcOptUrl || '',
        image: ds.vgcOptImage || '',
        price: parseInt(ds.vgcOptPrice, 10) || 0
      };
      if (det.id && det.price) {
        optimistic = true;
        document.dispatchEvent(new CustomEvent('vg:cart:opt-add', { detail: det }));
      }
    }

    fetch('/cart/add.js', { method: 'POST', body: new FormData(form) })
      .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(function () {
        if (optimistic) document.dispatchEvent(new CustomEvent('vg:cart:opt-settle'));
        else document.dispatchEvent(new CustomEvent('vg:cart:changed'));
      })
      .catch(function () {
        if (optimistic) document.dispatchEvent(new CustomEvent('vg:cart:opt-settle'));
        form.submit();
      });
  }, false);

  // Cart links open the drawer instead of navigating to /cart
  document.addEventListener('click', function (e) {
    var a = e.target.closest ? e.target.closest('a[href="/cart"], a[href^="/cart?"], a[href$="/cart"]') : null;
    if (!a) return;
    e.preventDefault();
    document.dispatchEvent(new CustomEvent('vg:cart:open'));
  }, true);

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-vgc]').forEach(init);
  });
})();
